
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Bot, Brain, Send, Sparkles, Info, CheckCircle, BarChart3, TrendingUp, AlertCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";

const AIAssistance = () => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('insight');
  const [response, setResponse] = useState<Record<string, string>>({
    insight: '',
    recommendation: '',
    analysis: ''
  });
  const { toast } = useToast();

  // Predefined prompts for users to select
  const suggestedPrompts = [
    "Analyze the performance of Program A over the last month",
    "Compare transaction volumes across all programs",
    "Identify trends in member engagement",
    "Recommend ways to increase program participation",
    "Analyze which merchants have the highest transaction volume",
    "How can I improve member retention?",
    "What rewards are most popular in my programs?"
  ];

  // Preconfigured insights for different categories
  const preconfiguredResponses = {
    // Performance insights
    performance: {
      insight: "Your program performance has improved by 18% compared to last quarter. The Employee Recognition program is your top performer with 34% growth in participation. Your average engagement rate is now 72%, which is 15% above industry average.",
      recommendation: "To further improve program performance, consider implementing recognition milestones that unlock special rewards. Based on your data, programs with clear milestone achievements show 23% higher engagement. Also, featuring top performers in a monthly spotlight increases participation by up to 30%.",
      analysis: "The data shows clear day-of-week patterns in your program usage. Mondays and Fridays have the highest activity, with 28% and 32% of weekly transactions respectively. Your current reward-to-participation ratio is 1:4.3, which is healthy compared to the industry benchmark of 1:5."
    },
    
    // Engagement insights
    engagement: {
      insight: "Member engagement has been steadily increasing, with a 23% rise in daily active users. The most engaged demographic is your technical team (76% participation), followed by sales (62%). Your gamification elements have driven a 41% increase in repeat participation.",
      recommendation: "For maximum engagement growth, introduce team challenges that create healthy competition. Teams that participate in monthly challenges show 47% higher retention rates. Consider adding 'streak bonuses' that reward consistent participation, which typically boost return rates by 35%.",
      analysis: "Analyzing engagement patterns shows that 68% of members who participate in at least three consecutive weekly events become long-term active users. Your current engagement funnel shows a 54% conversion rate from casual to active participants, with the biggest drop-off after the first interaction."
    },
    
    // Transaction insights
    transactions: {
      insight: "Transaction volume has increased by 27% this quarter. The average transaction size is 45 points, with technology department redemptions averaging 65 points per transaction. Peak transaction times are Tuesdays between 11am-2pm and Thursdays between 3pm-5pm.",
      recommendation: "Consider optimizing reward offerings around your peak transaction times with 'happy hour' bonuses. Programs implementing time-based incentives see an average 31% volume increase. Additionally, introducing tiered redemption options could increase your average transaction size by approximately 20%.",
      analysis: "Transaction data reveals that 72% of your members prefer experience-based rewards over physical items. There's a strong correlation between transaction amount and member tenure – users active for 6+ months spend 40% more points per transaction than new users. Your transaction completion rate is 94%, well above the industry average of 88%."
    },
    
    // Merchant insights
    merchants: {
      insight: "Your top performing merchant partners are TechStore (32% of redemptions), TravelPlus (24%), and DineDelight (18%). Merchants featuring exclusive offers generate 37% more transactions than those with standard offerings. Mobile redemptions have overtaken desktop by 58% to 42%.",
      recommendation: "Expand your partnership with TechStore and TravelPlus through featured promotional campaigns. Exclusive partner promotions typically generate 45% higher engagement. For DineDelight, consider implementing a special weekend promotion to capitalize on their peak business hours, which could increase redemptions by up to 25%.",
      analysis: "Analysis of merchant performance shows seasonal patterns with technology purchases peaking in Q4 (38% annual volume), travel rewards strongest in Q2 (42%), and dining consistently performing throughout the year with minor peaks around holidays (15% increase). Merchant-specific redemption rates vary from 68% to 94%, with an average fulfillment time of 2.3 days."
    },
    
    // Growth insights
    growth: {
      insight: "Your program is growing at 3.2% month-over-month, with new member acquisition costs decreasing by 17%. The referral program has contributed 28% of new members, making it your most efficient growth channel. Retention rates have improved from 76% to 84% year-over-year.",
      recommendation: "To accelerate growth, implement a multi-tier referral program that rewards both referrers and new members. Such programs typically increase referral conversion by 35%. Also consider creating a 'fast start' program for new members that guides them through their first redemption experience, which can increase long-term retention by 24%.",
      analysis: "Growth pattern analysis shows that members who make their first redemption within two weeks of joining have a 76% higher lifetime value. Your current member acquisition funnel converts at 12% from awareness to registration, with the biggest drop-off occurring at the program education stage (42% abandonment)."
    }
  };

  const handlePromptSubmit = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Empty prompt",
        description: "Please enter a prompt to get insights.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call to an AI service
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Determine which category to use based on the prompt
      let responseCategory = 'performance'; // default
      
      const promptLower = prompt.toLowerCase();
      
      if (promptLower.includes('engage') || promptLower.includes('participation') || promptLower.includes('active')) {
        responseCategory = 'engagement';
      } else if (promptLower.includes('transaction') || promptLower.includes('redemption') || promptLower.includes('points')) {
        responseCategory = 'transactions';
      } else if (promptLower.includes('merchant') || promptLower.includes('partner') || promptLower.includes('store')) {
        responseCategory = 'merchants';
      } else if (promptLower.includes('growth') || promptLower.includes('increase') || promptLower.includes('expand')) {
        responseCategory = 'growth';
      }
      
      // Get the preconfigured responses for the selected category
      const selectedResponses = preconfiguredResponses[responseCategory as keyof typeof preconfiguredResponses];
      
      // Personalize the responses with the user's prompt
      const personalizedResponses = {
        insight: `Based on your query about "${prompt}", here's what I found: ${selectedResponses.insight}`,
        recommendation: `For your question about "${prompt}", I recommend: ${selectedResponses.recommendation}`,
        analysis: `Detailed analysis for "${prompt}": ${selectedResponses.analysis}`
      };
      
      setResponse(personalizedResponses);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate insights. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleSuggestedPrompt = (prompt: string) => {
    setPrompt(prompt);
  };

  // Function to render response content with icons
  const renderResponseContent = (content: string, type: string) => {
    if (!content) return null;
    
    let icon;
    let color;
    
    switch (type) {
      case 'insight':
        icon = <Info className="h-5 w-5 text-blue-500" />;
        color = "text-blue-800";
        break;
      case 'recommendation':
        icon = <CheckCircle className="h-5 w-5 text-green-500" />;
        color = "text-green-800";
        break;
      case 'analysis':
        icon = <BarChart3 className="h-5 w-5 text-purple-500" />;
        color = "text-purple-800";
        break;
      default:
        icon = <Info className="h-5 w-5" />;
        color = "text-gray-800";
    }
    
    // Split the content by sentences and add some formatting
    const sentences = content.split('. ');
    
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          {icon}
          <h3 className={`font-medium ${color} capitalize`}>{type}</h3>
        </div>
        
        {sentences.map((sentence, index) => {
          if (!sentence.trim()) return null;
          
          // Add the period back except for the last item if it doesn't already have one
          const formattedSentence = index < sentences.length - 1 && !sentence.endsWith('.') 
            ? sentence + '.' 
            : sentence;
            
          return (
            <div key={index} className="flex gap-2 items-start mb-2">
              <div className="mt-1 text-gray-400">•</div>
              <p>{formattedSentence}</p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="container mx-auto py-6">
      <h1 className="text-3xl font-bold mb-6">AI Assistance</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5" />
              Prompt Assistant
            </CardTitle>
            <CardDescription>
              Ask questions about your programs, transactions, and member engagement
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea 
              placeholder="What would you like to know about your programs?" 
              className="min-h-[120px]"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </CardContent>
          <CardFooter className="flex flex-col items-start gap-4">
            <Button 
              className="w-full" 
              onClick={handlePromptSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>Generating Insights...</>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Generate Insights
                </>
              )}
            </Button>
            
            <div className="w-full">
              <p className="text-sm font-medium mb-2">Try these prompts:</p>
              <ScrollArea className="h-[180px]">
                <div className="flex flex-col gap-2">
                  {suggestedPrompts.map((suggestedPrompt, index) => (
                    <Button 
                      key={index} 
                      variant="outline" 
                      className="justify-start text-left h-auto py-2 px-3"
                      onClick={() => handleSuggestedPrompt(suggestedPrompt)}
                    >
                      <Sparkles className="h-3 w-3 mr-2 text-blue-500" />
                      {suggestedPrompt}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </CardFooter>
        </Card>
        
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              AI Insights
            </CardTitle>
            <CardDescription>
              Personalized analysis based on your program data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs 
              defaultValue="insight" 
              value={activeTab} 
              onValueChange={handleTabChange}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="insight">Insights</TabsTrigger>
                <TabsTrigger value="recommendation">Recommendations</TabsTrigger>
                <TabsTrigger value="analysis">Analysis</TabsTrigger>
              </TabsList>
              
              <ScrollArea className="h-[350px] rounded-md border p-4 mt-4">
                <TabsContent value="insight" className="mt-0">
                  {response.insight ? (
                    renderResponseContent(response.insight, 'insight')
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[300px] text-center">
                      <TrendingUp className="h-12 w-12 text-gray-300 mb-4" />
                      <p className="text-gray-500">Enter a prompt and click "Generate Insights" to get started</p>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="recommendation" className="mt-0">
                  {response.recommendation ? (
                    renderResponseContent(response.recommendation, 'recommendation')
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[300px] text-center">
                      <Bot className="h-12 w-12 text-gray-300 mb-4" />
                      <p className="text-gray-500">Enter a prompt and click "Generate Insights" to get recommendations</p>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="analysis" className="mt-0">
                  {response.analysis ? (
                    renderResponseContent(response.analysis, 'analysis')
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[300px] text-center">
                      <AlertCircle className="h-12 w-12 text-gray-300 mb-4" />
                      <p className="text-gray-500">Enter a prompt and click "Generate Insights" to get detailed analysis</p>
                    </div>
                  )}
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIAssistance;
